import * as m from '@staiter/i18n';
import type { SnapshotTask } from '@staiter/shared/task-module';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Folder,
  Info,
  ListChecks,
  MoreHorizontal,
  Pencil,
  Play,
  RefreshCw,
} from 'lucide-react';
import { Fragment, type JSX, type ReactNode, useMemo, useRef, useState } from 'react';
import { Breadcrumbs, EmptyState, PageHeader } from '~/components/data';
import { Button, ConfirmDialog } from '~/components/primitives';
import { MaterializeDialog } from '~/components/tasks/blueprints/MaterializeDialog';
import { SKIP_LABEL_TEXT } from '~/components/tasks/blueprints/TemplateOpSummary';
import { ApiRequestError } from '~/lib/api/client';
import { formatDate, formatDecimal } from '~/lib/format';
import {
  type BlueprintDetail,
  type BlueprintVersion,
  activateBlueprintVersion,
  convertBlueprintToV2,
  deactivateBlueprint,
  deleteBlueprint,
  fetchBlueprint,
  fetchBlueprintVersions,
} from '~/lib/tasks/api/blueprints';
import {
  DEPENDENCY_TYPE_LABEL,
  FIELD_TYPE_LABEL,
  PROJECT_KIND_LABEL,
  STATE_GROUP_LABEL,
  TASK_PRIORITY_LABEL,
  formatEstimate,
  labelOf,
} from '~/lib/tasks/i18n/labels';
import shared from './_auth._shared.module.css';
import styles from './_auth.work-blueprints_.$id.module.css';

/**
 * `/work-blueprints/$id` — просмотр шаблона (фаза B, SOLUTION §3).
 *
 * До этой страницы шаблон был чёрной коробкой: `GET /blueprints/:id` отдавал
 * полный снапшот, а UI использовал его только для anchor-превью диалога.
 * Убедиться, что внутри нет привязки к прошлым периодам, пользователю было негде —
 * и претензия «шаблон сохраняет даты» была неопровержима именно поэтому.
 *
 * Инвариант I-C: НИ ОДНОЙ абсолютной даты содержимого. План показывается только
 * как «День N» и длительности; абсолютные даты — лишь у метаданных строки
 * (создан/обновлён) и появляются в превью материализации после ввода якоря.
 *
 * Progressive Disclosure (§3): primary — шапка + дерево; вехи, статусы, поля,
 * связи, сводка пропущенного и версии свёрнуты в аккордеоны.
 *
 * Flat-роут `_auth.work-blueprints_.$id` (прецедент `_auth.admin.integrations_.platform`):
 * список остаётся leaf'ом, детальная не наследует его layout.
 */

const LIST_KEY = ['task-module', 'blueprints'];

export const Route = createFileRoute('/_auth/work-blueprints_/$id')({
  component: BlueprintViewPage,
});

// ─── Дерево задач ────────────────────────────────────────────────────

type TreeNode = { task: SnapshotTask; children: TreeNode[] };

/** `SnapshotAssignee` экспортируется как схема, но не как тип — выводим из задачи. */
type SnapshotAssignee = SnapshotTask['assignees'][number];

/**
 * Снапшот хранит иерархию как `parent_key`, порядок — как `sort_order`.
 * Ссылка на несуществующего родителя не должна ронять страницу и не должна
 * прятать задачу: такой узел поднимается в корень (гвард B5 это состояние
 * запрещает на входе ручек, но старые снапшоты писались до гвардов).
 */
function buildTree(tasks: readonly SnapshotTask[]): TreeNode[] {
  const byKey = new Map<string, TreeNode>();
  for (const task of tasks) byKey.set(task.key, { task, children: [] });

  const roots: TreeNode[] = [];
  for (const node of byKey.values()) {
    const parentKey = node.task.parent_key;
    const parent = parentKey === null ? undefined : byKey.get(parentKey);
    if (parent && parent !== node) parent.children.push(node);
    else roots.push(node);
  }

  const sortRec = (nodes: TreeNode[]): void => {
    nodes.sort((a, b) => a.task.sort_order - b.task.sort_order);
    for (const n of nodes) sortRec(n.children);
  };
  sortRec(roots);
  return roots;
}

/**
 * Дерево разворачивается в плоский список с уровнем: сетка колонок общая для
 * всех строк, а вложенные `<ul>` завели бы каждому уровню свой контейнер и
 * рассинхронизировали бы колонки (F-B1). Иерархия остаётся в `aria-level`.
 */
type FlatNode = { task: SnapshotTask; depth: number };

function flattenTree(nodes: TreeNode[], depth = 0, out: FlatNode[] = []): FlatNode[] {
  for (const n of nodes) {
    out.push({ task: n.task, depth });
    flattenTree(n.children, depth + 1, out);
  }
  return out;
}

function TaskTree({
  nodes,
  userNames,
  labelNames,
}: {
  nodes: FlatNode[];
  userNames: Map<string, string>;
  labelNames: Map<string, string>;
}): JSX.Element {
  return (
    <>
      <div className={styles.treeHead} aria-hidden="true">
        <span>{m.tmpl_view_col_task()}</span>
        <span className={styles.headCellNum}>{m.tmpl_view_col_start()}</span>
        <span className={styles.headCellNum}>{m.tmpl_view_col_due()}</span>
        <span className={styles.headCellNum}>{m.tmpl_view_col_duration()}</span>
        <span />
      </div>
      <ul className={styles.tree} role="tree" aria-label={m.tmpl_view_section_tasks()}>
        {nodes.map(({ task, depth }) => (
          <li key={task.key} role="treeitem" aria-level={depth + 1}>
            <TaskNode task={task} depth={depth} userNames={userNames} labelNames={labelNames} />
          </li>
        ))}
      </ul>
    </>
  );
}

function assigneeLabel(a: SnapshotAssignee, userNames: Map<string, string>): string {
  if (a.type === 'user') {
    // Незнакомый UUID — человек, вышедший из компании после сохранения шаблона.
    // Показывать «сырой» uuid бессмысленно, скрывать — врать о составе.
    return userNames.get(a.user_id) ?? m.tmpl_view_former_member();
  }
  if (a.type === 'role') return m.tmpl_view_assignee_role({ role: a.role_key });
  return m.tmpl_view_assignee_group();
}

function TaskNode({
  task,
  depth,
  userNames,
  labelNames,
}: {
  task: SnapshotTask;
  depth: number;
  userNames: Map<string, string>;
  labelNames: Map<string, string>;
}): JSX.Element {
  const isFolder = task.kind === 'folder';
  const start = task.start_offset_days;
  const due = task.due_offset_days;
  // Папка своих дат не имеет по инварианту — у неё колонки пустые, а не «без дат»:
  // это структура, а не пробел в плане.
  const dateless = !isFolder && start === null && due === null;

  return (
    <div className={styles.node}>
      <div className={styles.nodeTitleCell}>
        {Array.from({ length: depth }, (_, i) => (
          <span key={`${task.key}-g-${i}`} className={styles.indentGuide} aria-hidden="true" />
        ))}
        {isFolder ? (
          <Folder size={14} className={styles.nodeIcon} aria-hidden="true" />
        ) : (
          <ListChecks size={14} className={styles.nodeIcon} aria-hidden="true" />
        )}
        <span className={isFolder ? styles.nodeTitleFolder : styles.nodeTitle} title={task.title}>
          {task.title}
        </span>
      </div>

      {/* F-B12 — пустая ячейка получает «—»: полностью пустая колонка читается
          как «данные не доехали», а прочерк — как «здесь этого нет». */}
      <span className={dateless ? styles.offsetCellMuted : styles.offsetCell}>
        {dateless
          ? m.tmpl_view_task_no_dates()
          : start !== null
            ? m.tmpl_view_day({ day: start })
            : '—'}
      </span>
      <span className={due !== null ? styles.offsetCell : styles.offsetCellMuted}>
        {due !== null ? m.tmpl_view_day({ day: due }) : '—'}
      </span>
      <span className={start !== null && due !== null ? styles.offsetCell : styles.offsetCellMuted}>
        {start !== null && due !== null ? m.tmpl_view_duration({ days: due - start + 1 }) : '—'}
      </span>

      <div className={styles.chips}>
        {task.priority !== 'none' && (
          <span className={styles.chip}>
            {labelOf(TASK_PRIORITY_LABEL, task.priority as never)}
          </span>
        )}
        {task.estimate_value !== null && task.estimate_uom !== null && (
          // F-B9 — подпись обязательна: без неё «3 дн.» (длительность) и «8 ч»
          // (оценка трудозатрат) стоят рядом как однородные, и читателю
          // приходится догадываться, что есть что.
          <span className={styles.chip}>
            {m.tmpl_view_estimate({
              value: formatEstimate(task.estimate_value, task.estimate_uom),
            })}
          </span>
        )}
        {/* F-B13 — чип ассайни подписан: в одном ряду с приоритетом и оценкой
            голое имя читается как ещё один атрибут неизвестной природы. */}
        {task.assignees.map((a, i) => (
          <span key={`${task.key}-a-${i}`} className={styles.chip}>
            {m.tmpl_view_assignee_chip({ who: assigneeLabel(a, userNames) })}
          </span>
        ))}
        {task.label_keys.map((k) => (
          <span key={`${task.key}-l-${k}`} className={styles.chipLabel}>
            {labelNames.get(k) ?? k}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Аккордеон ───────────────────────────────────────────────────────

function Accordion({
  title,
  count,
  children,
}: {
  title: string;
  count?: number;
  children: ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <section className={styles.accordion}>
      <button
        type="button"
        className={styles.accordionHeader}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? (
          <ChevronDown size={14} aria-hidden="true" />
        ) : (
          <ChevronRight size={14} aria-hidden="true" />
        )}
        {title}
        {count !== undefined && <span className={styles.accordionCount}>{count}</span>}
      </button>
      {open && <div className={styles.accordionBody}>{children}</div>}
    </section>
  );
}

// ─── Страница ────────────────────────────────────────────────────────

function BlueprintViewPage(): JSX.Element {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [materializing, setMaterializing] = useState(false);
  const [confirmLifecycle, setConfirmLifecycle] = useState<'deactivate' | 'delete' | null>(null);
  const [restoring, setRestoring] = useState<BlueprintVersion | null>(null);
  const [converting, setConverting] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: [...LIST_KEY, id],
    queryFn: () => fetchBlueprint(id),
  });
  const versions = useQuery({
    queryKey: [...LIST_KEY, id, 'versions'],
    queryFn: () => fetchBlueprintVersions(id),
  });

  const bp = detail.data;
  const snapshot = bp?.snapshot ?? null;
  const isLegacy = bp !== undefined && snapshot === null;

  // B15 — имена приезжают денормом вместе с ответами detail и versions. Раньше
  // страница тянула ради них весь пул `assignable-users` компании отдельным
  // запросом; сейчас лишнего round-trip нет.
  const userNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of bp?.assignee_directory ?? []) map.set(u.user_id, u.display_name);
    for (const u of versions.data?.directory ?? []) map.set(u.user_id, u.display_name);
    return map;
  }, [bp, versions.data]);

  const labelNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of snapshot?.labels ?? []) map.set(l.key, l.name);
    return map;
  }, [snapshot]);

  const taskTitles = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of snapshot?.tasks ?? []) map.set(t.key, t.title);
    return map;
  }, [snapshot]);

  const tree = useMemo(() => flattenTree(buildTree(snapshot?.tasks ?? [])), [snapshot]);

  /**
   * «план: N дней» — максимум по смещениям задач И вех (та же планка, что в B14).
   * У v1-строки снапшота нет, но план есть: он лежит в плоских `tasks`
   * (`start_offset_days + duration_days − 1`). Считать только по снапшоту значило
   * бы показывать «нет дат» шаблону, у которого прямо под бейджем видно «День 0».
   */
  const maxOffset = useMemo(() => {
    let max: number | null = null;
    const bump = (v: number | null) => {
      if (v !== null && (max === null || v > max)) max = v;
    };
    if (snapshot) {
      for (const t of snapshot.tasks) {
        bump(t.start_offset_days);
        bump(t.due_offset_days);
      }
      for (const ms of snapshot.milestones) bump(ms.deadline_offset_days);
      return max;
    }
    for (const t of bp?.tasks ?? []) {
      bump(t.start_offset_days + Math.max(1, t.duration_days) - 1);
    }
    return max;
  }, [snapshot, bp]);

  const invalidate = (): void => {
    qc.invalidateQueries({ queryKey: LIST_KEY });
  };

  const lifecycle = useMutation({
    mutationFn: ({ hard }: { hard: boolean }) =>
      hard ? deleteBlueprint(id) : deactivateBlueprint(id),
    onSuccess: () => {
      setConfirmLifecycle(null);
      invalidate();
      navigate({ to: '/work-blueprints' });
    },
  });

  const restore = useMutation({
    mutationFn: (versionId: string) => activateBlueprintVersion(versionId),
    onSuccess: (created) => {
      setRestoring(null);
      setErrorCode(null);
      invalidate();
      navigate({ to: '/work-blueprints/$id', params: { id: created.id } });
    },
    onError: (e) => setErrorCode(e instanceof ApiRequestError ? e.code : 'server.internal'),
  });

  const convert = useMutation({
    mutationFn: () => convertBlueprintToV2(id),
    onSuccess: (created) => {
      setConverting(false);
      setErrorCode(null);
      invalidate();
      navigate({ to: '/work-blueprints/$id', params: { id: created.id } });
    },
    onError: (e) => setErrorCode(e instanceof ApiRequestError ? e.code : 'server.internal'),
  });

  if (detail.isPending) {
    return (
      <div className={shared.page}>
        <p className={shared.subtle}>{m.tm_loading()}</p>
      </div>
    );
  }
  if (detail.isError || !bp) {
    return (
      <div className={shared.page}>
        <EmptyState
          title={m.tmpl_view_not_found_title()}
          description={m.tmpl_view_not_found_body()}
          action={
            <Link to="/work-blueprints">
              <Button variant="secondary">{m.tm_route_project_templates()}</Button>
            </Link>
          }
        />
      </div>
    );
  }

  const activeVersion = (versions.data?.versions ?? []).find((v) => v.is_active);
  const nextVersion = Math.max(0, ...(versions.data?.versions ?? []).map((v) => v.version)) + 1;

  return (
    <div className={shared.page}>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: m.tm_route_project_templates(), to: '/work-blueprints' },
              { label: bp.name },
            ]}
          />
        }
        title={bp.name}
        titleAdornment={
          <div className={styles.badges}>
            <span className={styles.badge}>v{bp.version}</span>
            {!bp.is_active && (
              <span className={[styles.badge, styles.badgeWarn].join(' ')}>
                {m.tmpl_view_badge_inactive()}
              </span>
            )}
            {isLegacy && (
              <span className={[styles.badge, styles.badgeWarn].join(' ')}>
                {m.tmpl_view_badge_legacy()}
              </span>
            )}
            <span className={[styles.badge, styles.badgeInfo].join(' ')}>
              {maxOffset === null
                ? m.tmpl_view_no_dates()
                : m.tmpl_view_plan_days({ days: maxOffset })}
            </span>
          </div>
        }
        description={
          <div className={shared.stack2}>
            {bp.description && <div>{bp.description}</div>}
            <div className={styles.metaRow}>
              <span className={styles.metaItem}>
                <span className={styles.metaLabel}>{m.tmpl_view_meta_kind()}</span>
                {labelOf(PROJECT_KIND_LABEL, bp.kind as never)}
              </span>
              {snapshot?.source && (
                <span className={styles.metaItem}>
                  <span className={styles.metaLabel}>{m.tmpl_view_meta_source()}</span>
                  {snapshot.source.project_name}
                  {snapshot.source.project_code ? ` · ${snapshot.source.project_code}` : ''}
                </span>
              )}
              <span className={styles.metaItem}>
                <span className={styles.metaLabel}>{m.tmpl_view_meta_author()}</span>
                {userNames.get(bp.created_by_user_id) ?? m.tmpl_view_former_member()}
              </span>
              <span className={styles.metaItem}>
                <span className={styles.metaLabel}>{m.tmpl_view_meta_updated()}</span>
                {formatDate(bp.updated_at)}
              </span>
            </div>
          </div>
        }
        actions={
          <div className={shared.rowCenter}>
            {/* v1-шаблон разворачивается в проект так же, как v2 (у материализатора
                своя v1-ветвь) — перевод в новый формат заменяет ПРАВКУ, а не
                создание проекта. Убрать отсюда CTA значило бы, что со страницы
                шаблона он недоступен, а из списка — доступен. */}
            {isLegacy && (
              <Button
                variant="secondary"
                leadingIcon={<RefreshCw size={14} />}
                onClick={() => setConverting(true)}
              >
                {m.tmpl_view_convert_action()}
              </Button>
            )}
            {/*
             * F-B4 — CTA по фактическому поведению бэкенда (SOLUTION §4.4 ред. 3):
             *   • v2 разворачивает ИМЕННО просматриваемую версию — это принятая
             *     фича, поэтому кнопка остаётся, но подписана версией: в точке
             *     необратимого действия должно быть видно, что именно поедет;
             *   • v1 такую материализацию запрещает (B16: 422
             *     `blueprint_not_active`) — кнопки, ведущей в гарантированную
             *     ошибку, здесь нет.
             */}
            {/* F7 — вход в редактор. В фазе B кнопки не было намеренно:
                редактора не существовало, и вести было некуда. v1-формат
                правится только через перевод в новый формат (§3). */}
            {!isLegacy && bp.is_active && (
              <Link to="/work-blueprints/$id/edit" params={{ id }}>
                <Button variant="secondary" leadingIcon={<Pencil size={14} />}>
                  {m.tmpl_view_edit_action()}
                </Button>
              </Link>
            )}
            {!(isLegacy && !bp.is_active) && (
              <Button
                leadingIcon={<Play size={14} />}
                // F-B15 — подсказка снимает вопрос «из какой версии», не тратя
                // на него место в подписи кнопки.
                title={
                  bp.is_active
                    ? m.tmpl_view_cta_tooltip_active({ version: bp.version })
                    : m.tmpl_view_cta_tooltip_inactive({ version: bp.version })
                }
                onClick={() => setMaterializing(true)}
              >
                {bp.is_active
                  ? m.tmpl_cta_create_project()
                  : m.tmpl_view_cta_create_from_version({ version: bp.version })}
              </Button>
            )}
            <OverflowMenu
              onDeactivate={() => setConfirmLifecycle('deactivate')}
              onDelete={() => setConfirmLifecycle('delete')}
            />
          </div>
        }
      />

      {/* Бейдж-объяснение — primary-уровень: ровно он снимает претензию
          «шаблон сохраняет даты», поэтому виден без раскрытия секций. */}
      <div className={styles.offsetNotice}>
        <Info size={16} aria-hidden="true" />
        <span>{m.tmpl_view_offsets_only()}</span>
      </div>

      {/* F-B7 — объяснение стоит рядом с тем, что объясняет (бейдж старого формата
          и кнопка перевода), а не последней строкой под «Версиями», куда его
          никто не свяжет с этими двумя элементами. */}
      {isLegacy && (
        <div className={styles.legacyNotice}>
          <AlertTriangle size={16} aria-hidden="true" />
          <span>{m.tmpl_view_legacy_hint()}</span>
        </div>
      )}

      {/* F-B4 — на неактивной v2 материализуется просматриваемая версия, поэтому
          говорим об этом до нажатия, а не после. На неактивной v1 CTA нет —
          объясняем, почему и куда идти. */}
      {!bp.is_active && !isLegacy && (
        <div className={styles.legacyNotice}>
          <AlertTriangle size={16} aria-hidden="true" />
          <span>{m.tmpl_view_inactive_notice({ version: bp.version })}</span>
        </div>
      )}
      {!bp.is_active && isLegacy && (
        <div className={styles.legacyNotice}>
          <AlertTriangle size={16} aria-hidden="true" />
          <span>{m.tmpl_view_inactive_legacy_notice()}</span>
        </div>
      )}

      {errorCode && <div className={styles.error}>{m.error_by_code(errorCode)}</div>}

      {isLegacy ? (
        <LegacyTasks tasks={bp.tasks} />
      ) : tree.length === 0 ? (
        <EmptyState
          title={m.tmpl_view_no_tasks_title()}
          description={m.tmpl_view_no_tasks_body()}
        />
      ) : (
        <section>
          <h2 className={shared.sectionTitle}>{m.tmpl_view_section_tasks()}</h2>
          <div className={styles.tree}>
            <TaskTree nodes={tree} userNames={userNames} labelNames={labelNames} />
          </div>
        </section>
      )}

      <div className={styles.accordions}>
        {snapshot && snapshot.milestones.length > 0 && (
          <Accordion title={m.tmpl_view_section_milestones()} count={snapshot.milestones.length}>
            <ul className={styles.rows}>
              {snapshot.milestones.map((ms) => (
                <li key={ms.key} className={styles.row}>
                  <span>{ms.name}</span>
                  <span className={styles.chip}>
                    {ms.deadline_offset_days === null
                      ? m.tmpl_view_task_no_dates()
                      : m.tmpl_view_day({ day: ms.deadline_offset_days })}
                  </span>
                  {ms.billing_pct !== null && (
                    <span className={styles.chip}>
                      {/* numeric(6,3) приезжает как '30.000' — хвостовые нули
                          в проценте оплаты читаются как ложная точность. */}
                      {m.tmpl_view_milestone_billing({ pct: formatDecimal(ms.billing_pct) })}
                    </span>
                  )}
                  {ms.linked_task_key !== null && (
                    <span className={styles.rowMuted}>
                      {m.tmpl_view_milestone_linked({
                        task: taskTitles.get(ms.linked_task_key) ?? ms.linked_task_key,
                      })}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </Accordion>
        )}

        {snapshot && snapshot.states.length > 0 && (
          <Accordion title={m.tmpl_view_section_states()} count={snapshot.states.length}>
            <ul className={styles.rows}>
              {snapshot.states.map((st) => (
                <li key={st.key} className={styles.row}>
                  <span
                    className={styles.swatch}
                    style={{
                      background: `var(--column-color-${st.color_token}, var(--color-border))`,
                    }}
                    aria-hidden="true"
                  />
                  <span>{st.name}</span>
                  <span className={styles.rowMuted}>
                    {labelOf(STATE_GROUP_LABEL, st.group as never)}
                  </span>
                  {st.is_default && (
                    <span className={styles.chip}>{m.tmpl_view_state_default()}</span>
                  )}
                </li>
              ))}
            </ul>
          </Accordion>
        )}

        {snapshot && snapshot.field_definitions.length > 0 && (
          <Accordion title={m.tmpl_view_section_fields()} count={snapshot.field_definitions.length}>
            <ul className={styles.rows}>
              {snapshot.field_definitions.map((f) => (
                <li key={f.key} className={styles.row}>
                  <span>{f.label}</span>
                  <span className={styles.rowMuted}>
                    {labelOf(FIELD_TYPE_LABEL, f.type as never)}
                  </span>
                  {f.required && (
                    <span className={styles.chip}>{m.tmpl_view_field_required()}</span>
                  )}
                </li>
              ))}
            </ul>
          </Accordion>
        )}

        {snapshot && snapshot.dependencies.length > 0 && (
          <Accordion title={m.tmpl_view_section_deps()} count={snapshot.dependencies.length}>
            <ul className={styles.rows}>
              {snapshot.dependencies.map((d, i) => (
                <li key={`${d.from_key}-${d.to_key}-${i}`} className={styles.row}>
                  <span>{taskTitles.get(d.from_key) ?? d.from_key}</span>
                  <span aria-hidden="true">→</span>
                  <span>{taskTitles.get(d.to_key) ?? d.to_key}</span>
                  <span className={styles.rowMuted}>
                    {labelOf(DEPENDENCY_TYPE_LABEL, d.relation_type as never)}
                  </span>
                </li>
              ))}
            </ul>
          </Accordion>
        )}

        {/* Сводка пропущенного осмысленна только у версий, СОХРАНЁННЫХ из проекта:
            у шаблона с нуля пропускать было нечего, и нули читались бы как дефект. */}
        {snapshot?.source && <SkippedSection skipped={snapshot.skipped} />}

        <Accordion
          title={m.tmpl_view_section_versions()}
          count={versions.data?.versions.length ?? 0}
        >
          {versions.isPending ? (
            <p className={shared.subtle}>{m.tm_loading()}</p>
          ) : (versions.data?.versions ?? []).length === 0 ? (
            <p className={shared.subtle}>{m.tmpl_view_versions_empty()}</p>
          ) : (
            <ul className={styles.rows}>
              {(versions.data?.versions ?? [])
                .slice()
                .reverse()
                .map((v) => (
                  <li
                    key={v.id}
                    className={[styles.versionRow, v.id === id ? styles.versionRowCurrent : '']
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <Link
                      to="/work-blueprints/$id"
                      params={{ id: v.id }}
                      className={styles.versionLink}
                    >
                      v{v.version}
                    </Link>
                    <span>{v.name}</span>
                    {v.is_active && (
                      <span className={styles.chip}>{m.tmpl_view_version_active()}</span>
                    )}
                    <span className={styles.versionMeta}>
                      {m.tm_route_tasks_count({ count: v.task_count })} · {formatDate(v.created_at)}{' '}
                      · {userNames.get(v.created_by_user_id) ?? m.tmpl_view_former_member()}
                    </span>
                    {!v.is_active && (
                      <span className={styles.versionActions}>
                        <Button variant="ghost" size="sm" onClick={() => setRestoring(v)}>
                          {m.tmpl_view_restore_action()}
                        </Button>
                      </span>
                    )}
                  </li>
                ))}
            </ul>
          )}
        </Accordion>
      </div>

      {materializing && (
        <MaterializeDialog
          blueprint={{ id: bp.id, name: bp.name }}
          onClose={() => setMaterializing(false)}
        />
      )}

      {confirmLifecycle && (
        <ConfirmDialog
          open
          variant={confirmLifecycle === 'delete' ? 'danger' : 'default'}
          title={
            confirmLifecycle === 'delete'
              ? m.tmpl_delete_confirm_title()
              : m.tmpl_deactivate_confirm_title()
          }
          description={
            confirmLifecycle === 'delete'
              ? m.tmpl_delete_confirm_body()
              : m.tmpl_deactivate_confirm_body()
          }
          confirmLabel={
            confirmLifecycle === 'delete'
              ? m.tmpl_card_menu_delete()
              : m.tmpl_card_menu_deactivate()
          }
          cancelLabel={m.tm_cancel()}
          loading={lifecycle.isPending}
          onCancel={() => setConfirmLifecycle(null)}
          onConfirm={() => lifecycle.mutate({ hard: confirmLifecycle === 'delete' })}
        />
      )}

      {restoring && (
        <ConfirmDialog
          open
          // F-B8 — заголовок обещает ровно то, что делает кнопка: «сделать
          // активной». «Вернуться к этой версии» читалось как переключение,
          // хотя действие append-only и создаёт копию.
          title={m.tmpl_view_restore_confirm_title()}
          // Явная формулировка «будет создана копия», а не «переключим версию»:
          // restore append-only, и молчание об этом породило бы вопрос
          // «почему версий стало больше» ровно в момент отката.
          description={m.tmpl_view_restore_confirm_body({
            next: nextVersion,
            from: restoring.version,
            name: activeVersion?.name ?? bp.name,
          })}
          confirmLabel={m.tmpl_view_restore_action()}
          cancelLabel={m.tm_cancel()}
          loading={restore.isPending}
          onCancel={() => setRestoring(null)}
          onConfirm={() => restore.mutate(restoring.id)}
        />
      )}

      {converting && (
        <ConfirmDialog
          open
          title={m.tmpl_view_convert_confirm_title()}
          description={m.tmpl_view_convert_confirm_body()}
          confirmLabel={m.tmpl_view_convert_action()}
          cancelLabel={m.tm_cancel()}
          loading={convert.isPending}
          onCancel={() => setConverting(false)}
          onConfirm={() => convert.mutate()}
        />
      )}
    </div>
  );
}

/**
 * Сводка `skipped` — только ненулевые категории (нули читались бы как дефект).
 * Карта «код → строка» переиспользуется из `TemplateOpSummary`: вторая копия
 * молча разошлась бы с ней при добавлении skip-кода.
 */
function SkippedSection({ skipped }: { skipped: Record<string, number> }): JSX.Element | null {
  const entries = Object.entries(skipped).filter(([, v]) => typeof v === 'number' && v > 0);
  if (entries.length === 0) return null;
  return (
    <Accordion title={m.tmpl_view_section_skipped()} count={entries.length}>
      <p className={styles.legacyHint}>{m.tmpl_view_skipped_hint()}</p>
      {/* F-B6 — label↔value, а не «внешних связей: 2» одной строкой: счётчики
          в своей колонке, mono + tabular, выключка вправо — иначе сводку
          невозможно просмотреть по числам. */}
      <dl className={styles.skippedGrid}>
        {entries.map(([code, count]) => {
          const label = SKIP_LABEL_TEXT[code];
          return (
            <Fragment key={code}>
              <dt className={styles.skippedLabel}>{label ? label() : code}</dt>
              <dd className={styles.skippedValue}>{count}</dd>
            </Fragment>
          );
        })}
      </dl>
    </Accordion>
  );
}

/** v1-шаблон: плоский список задач read-only (SOLUTION §3, блок «v1-шаблоны»). */
function LegacyTasks({ tasks }: { tasks: BlueprintDetail['tasks'] }): JSX.Element {
  if (tasks.length === 0) {
    return (
      <EmptyState title={m.tmpl_view_no_tasks_title()} description={m.tmpl_view_no_tasks_body()} />
    );
  }
  return (
    <section>
      <h2 className={shared.sectionTitle}>{m.tmpl_view_section_tasks()}</h2>
      {/* Те же колонки, что у v2-дерева. «Срок» у старого формата не хранится —
          он выводится только при переводе в новый формат, поэтому колонка пуста. */}
      <div className={styles.treeHead} aria-hidden="true">
        <span>{m.tmpl_view_col_task()}</span>
        <span className={styles.headCellNum}>{m.tmpl_view_col_start()}</span>
        <span className={styles.headCellNum}>{m.tmpl_view_col_due()}</span>
        <span className={styles.headCellNum}>{m.tmpl_view_col_duration()}</span>
        <span />
      </div>
      <ul className={styles.tree}>
        {tasks.map((t) => (
          <li key={t.key}>
            <div className={styles.node}>
              <div className={styles.nodeTitleCell}>
                <ListChecks size={14} className={styles.nodeIcon} aria-hidden="true" />
                <span className={styles.nodeTitle} title={t.title}>
                  {t.title}
                </span>
              </div>
              <span className={styles.offsetCell}>
                {m.tmpl_view_day({ day: t.start_offset_days })}
              </span>
              <span className={styles.offsetCell} />
              <span className={styles.offsetCell}>
                {m.tmpl_view_duration({ days: t.duration_days })}
              </span>
              <div className={styles.chips}>
                {t.priority !== 'none' && (
                  <span className={styles.chip}>
                    {labelOf(TASK_PRIORITY_LABEL, t.priority as never)}
                  </span>
                )}
                {t.assignee_role !== null && (
                  <span className={styles.chip}>
                    {m.tmpl_view_assignee_role({ role: t.assignee_role })}
                  </span>
                )}
                {t.depends_on.length > 0 && (
                  <span className={[styles.chip, styles.chipMuted].join(' ')}>
                    {m.tmpl_view_depends_on({ count: t.depends_on.length })}
                  </span>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Overflow «…» — деактивация / удаление (перенос из карточки списка). */
function OverflowMenu({
  onDeactivate,
  onDelete,
}: {
  onDeactivate: () => void;
  onDelete: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  return (
    <div
      className={styles.menuWrap}
      ref={ref}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
      }}
    >
      <Button
        variant="ghost"
        size="sm"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={m.tmpl_view_more_actions()}
        onClick={() => setOpen((v) => !v)}
      >
        <MoreHorizontal size={16} />
      </Button>
      {open && (
        <div className={styles.menu} role="menu">
          <button
            type="button"
            role="menuitem"
            className={styles.menuItem}
            onClick={() => {
              setOpen(false);
              onDeactivate();
            }}
          >
            {m.tmpl_card_menu_deactivate()}
          </button>
          <button
            type="button"
            role="menuitem"
            className={styles.menuItemDanger}
            onClick={() => {
              setOpen(false);
              onDelete();
            }}
          >
            {m.tmpl_card_menu_delete()}
          </button>
        </div>
      )}
    </div>
  );
}
